import { useMsalToken } from "@/hooks/use-msal-token";
import { API_ENDPOINTS, TAG_BUILDER } from "./abstractions/api-types";
import { useAuthenticatedApi } from "./abstractions/use-authenticated-api";
import type {
  GetUserProfileRequest,
  GetUserProfileResponse,
  UploadUserDocumentResponse,
  UploadUserDocumentRequest,
  DeleteUserDocumentResponse,
  DeleteUserDocumentRequest,
  GetUserDocumentsResponse,
  GetUserDocumentsRequest,
  MarkNotificationReadRequest,
  MarkAllNotificationsReadRequest,
  DeleteUserAccountRequest,
  GetNotificationsRequest,
  GetNotificationsResponse
} from "@/domain/core";
import {
  buildHttpDelete,
  buildHttpGet,
  buildHttpPost
} from "@/utilities/api-utils";
import { config } from "@/config/environment";
import {
  DEFAULT_QUERY_OPTIONS,
  NOTIFICATION_QUERY_OPTIONS
} from "./abstractions/api-config";
import { DEFAULT_PAGE_SIZE } from "@/constants";

// Authenticated Users API hook
export function useAuthenticatedUsersApi() {
  const authenticatedApi = useAuthenticatedApi();
  const { addAuthHeader } = useMsalToken();

  const usersApi = authenticatedApi.injectEndpoints({
    overrideExisting: false,
    // Endpoints
    endpoints: (builder) => ({
      // Get user profile
      getUserProfile: builder.query<
        GetUserProfileResponse,
        GetUserProfileRequest
      >({
        query: async (request) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.USERS.PROFILE(request.userId)
          ),
        providesTags: (result) =>
          result
            ? [
                TAG_BUILDER.USER.ENTITY(result.userId),
                ...(result.professional?.id
                  ? [TAG_BUILDER.PROFESSIONAL.ENTITY(result.professional.id)]
                  : []),
                ...(result.practice?.id
                  ? [TAG_BUILDER.PRACTICE.ENTITY(result.practice.id)]
                  : [])
              ]
            : [],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get user documents
      getUserDocuments: builder.query<
        GetUserDocumentsResponse,
        GetUserDocumentsRequest
      >({
        query: async ({ userId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.USERS.DOCUMENTS(userId)
          ),
        providesTags: (_, __, { userId }) => TAG_BUILDER.USER.DOCUMENTS(userId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Upload user document
      uploadUserDocument: builder.mutation<
        UploadUserDocumentResponse,
        UploadUserDocumentRequest
      >({
        query: async ({ userId, formData }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.USERS.UPLOAD_DOCUMENT(userId),
            formData
          ),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Delete a practice document base on document ID
      deleteUserDocument: builder.mutation<
        DeleteUserDocumentResponse,
        DeleteUserDocumentRequest
      >({
        query: async ({ userId, documentId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.USERS.DOCUMENT(userId, documentId)
          ),
        invalidatesTags: (_, __, { userId }) =>
          TAG_BUILDER.USER.DOCUMENTS(userId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get the signed-in user's (or inspected user's) inbox
      getUserNotifications: builder.query<
        GetNotificationsResponse,
        GetNotificationsRequest
      >({
        query: async ({
          entityId,
          page = 1,
          pageSize = DEFAULT_PAGE_SIZE,
          isRead,
          searchTerm,
          sortBy,
          sortOrder
        }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.USERS.NOTIFICATIONS(entityId),
            { page, pageSize, isRead, searchTerm, sortBy, sortOrder }
          ),
        providesTags: (_, __, { entityId }) => [
          TAG_BUILDER.NOTIFICATION.FOR_ENTITY(entityId)
        ],
        ...NOTIFICATION_QUERY_OPTIONS
      }),

      // Mark notification as read
      markNotificationRead: builder.mutation<void, MarkNotificationReadRequest>({
        query: async ({ userId, notificationId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.USERS.MARK_NOTIFICATION_READ(userId, notificationId),
            {}
          ),
        invalidatesTags: (_, __, { invalidateContext }) =>
          invalidateContext
            ? [TAG_BUILDER.NOTIFICATION.FOR_ENTITY(invalidateContext.entityId)]
            : [],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Delete (deactivate) the signed-in user's account
      deleteUserAccount: builder.mutation<void, DeleteUserAccountRequest>({
        query: async ({ userId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.USERS.DELETE_ACCOUNT(userId)
          ),
        invalidatesTags: (_, __, { userId }) => [
          TAG_BUILDER.USER.ENTITY(userId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Mark all notifications as read
      markAllNotificationsRead: builder.mutation<
        void,
        MarkAllNotificationsReadRequest
      >({
        query: async ({ userId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.USERS.MARK_ALL_NOTIFICATIONS_READ(userId),
            {}
          ),
        invalidatesTags: (_, __, { invalidateContext }) =>
          invalidateContext
            ? [TAG_BUILDER.NOTIFICATION.FOR_ENTITY(invalidateContext.entityId)]
            : [],
        ...DEFAULT_QUERY_OPTIONS
      })
    })
  });

  return {
    useGetUserProfileQuery: usersApi.useGetUserProfileQuery,
    useGetUserDocumentsQuery: usersApi.useGetUserDocumentsQuery,
    useUploadUserDocumentMutation: usersApi.useUploadUserDocumentMutation,
    useDeleteUserDocumentMutation: usersApi.useDeleteUserDocumentMutation,
    useMarkNotificationReadMutation: usersApi.useMarkNotificationReadMutation,
    useMarkAllNotificationsReadMutation:
      usersApi.useMarkAllNotificationsReadMutation,
    useGetUserNotificationsQuery: usersApi.useGetUserNotificationsQuery,
    useDeleteUserAccountMutation: usersApi.useDeleteUserAccountMutation
  };
}
